/**
 * Phase 600: Runtime preset resolver.
 *
 * Fetches design-system preset tokens from the Cogo platform Worker
 * (GET /api/design-systems/:id) and caches results for TTL_MS milliseconds
 * to avoid redundant network round-trips on re-renders.
 */

const WORKER_BASE =
  (typeof import.meta !== "undefined" &&
    (import.meta as unknown as Record<string, unknown>).env &&
    ((import.meta as unknown as { env: Record<string, string> }).env
      .VITE_PLATFORM_BASE as string | undefined)) ??
  "https://platform.cogo.xyz";

const TTL_MS = 60_000;
const cache = new Map<string, { ts: number; tokens: unknown }>();

/**
 * Fetch preset tokens for `presetId` from the Worker.
 *
 * @param presetId - UUID of the design-system preset.
 * @param jwt      - Supabase access token; if null, returns null immediately.
 * @returns        - The `tokens` payload from the Worker response, or null on
 *                   any failure (network error, non-2xx, missing JWT, etc.).
 */
export async function fetchPresetTokens(
  presetId: string,
  jwt: string | null,
): Promise<unknown | null> {
  if (!jwt) return null;

  const hit = cache.get(presetId);
  if (hit && Date.now() - hit.ts < TTL_MS) return hit.tokens;

  try {
    const res = await fetch(
      `${WORKER_BASE}/api/design-systems/${encodeURIComponent(presetId)}`,
      {
        method: "GET",
        headers: { Authorization: `Bearer ${jwt}` },
      },
    );
    if (!res.ok) return null;
    const json = (await res.json()) as { tokens?: unknown };
    if (json.tokens === undefined) return null;
    cache.set(presetId, { ts: Date.now(), tokens: json.tokens });
    return json.tokens;
  } catch {
    return null;
  }
}

/**
 * Evict a single preset from the cache (or clear all if no ID given).
 * Primarily for testing; may also be used after a Studio Save.
 */
export function clearPresetCache(presetId?: string): void {
  if (presetId) cache.delete(presetId);
  else cache.clear();
}

// Cross-tab cache invalidation: Studio save → Starter render
// When Studio posts {type:"invalidate", presetId} on "cogo-preset-cache",
// drop the matching entry so the next render fetches fresh tokens.
if (typeof BroadcastChannel !== "undefined") {
  try {
    const ch = new BroadcastChannel("cogo-preset-cache");
    ch.onmessage = (e) => {
      const data = e.data as { type?: string; presetId?: string } | null;
      if (!data) return;
      if (data.type === "invalidate" && data.presetId) {
        cache.delete(data.presetId);
      } else if (data.type === "invalidate-all") {
        cache.clear();
      }
    };
  } catch {
    // ignore
  }
}
